import { vec3 } from 'gl-matrix'
import Program from 'nanogl/program'
import { GLContext } from 'nanogl/types'
import Node from 'nanogl-gltf/lib/elements/Node'
import Primitive from 'nanogl-gltf/lib/elements/Primitive'

import { RenderContext } from '@/webgl/core/RenderContext'
import Programs from '@/webgl/glsl/programs'
import { randomFloat } from '@/webgl/math'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import GltfParticles, { ParticleData } from '@/webgl/runner/rendering/GltfParticles'

export type StatusType = 'confusion'

const RES: Record<StatusType, string> = {
  confusion: 'common/confusion_particles.glb',
}

type StatusItem = {
  type: StatusType
  time: number
  duration: number
}

export default class PlayerSatusRenderer implements ILoadable {
  PCOUNT = 5

  node: Node = new Node()
  primitive: Primitive
  active: boolean = false
  program: Program

  _statuses: StatusType[] = []
  _renderers: Map<StatusType, GltfParticles> = new Map()

  _actives: StatusItem[] = []

  constructor(
    readonly gl: GLContext,
    readonly world: GameWorld
  ) {
    this.world.root.add(this.node)
    this.node.z = this.world.settings.GRAB_THRESHOLD
    this.world.events.StatusTrigger.on(this.trigger)
    this.world.events.GameDispose.on(this.dispose)
  }

  dispose = () => {
    this.world.events.StatusTrigger.off(this.trigger)
  }

  use(ids: StatusType[]) {
    this._statuses = ids
    this.active = true
  }

  async load(abort: AbortSignal): Promise<unknown> {
    if (!this.active) return Promise.resolve()

    this.program = Programs(this.gl).get('particles-std')

    for (const status of this._statuses) {
      const particles = new GltfParticles(this.gl, RES[status])
      await particles.load(abort)
      particles.program = this.program
      particles.cfg
        .enableBlend()
        .blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA)
        .enableCullface(true)
        .enableDepthTest(false)

      const data: ParticleData[] = []
      const globalScale = 0.5

      for (let i = 0; i < this.PCOUNT; i++) {
        const angle = (i / this.PCOUNT) * Math.PI + randomFloat(-0.3, 0.3)
        const x = Math.cos(angle) * 0.05
        const y = Math.sin(angle) * 0.05

        const position = vec3.fromValues(x, y, 0)
        const rotation = randomFloat(0, Math.PI * 2)

        const lifetime = 0.5
        const delay = 0.0

        const velocity = vec3.fromValues(x, y, 0)
        vec3.normalize(velocity, velocity)

        const speed = 0.1
        const scale = (Math.random() * 0.2 + 0.2) * globalScale
        const angularSpeed = Math.random() * 0.25 + 0.4

        data.push({
          position,
          velocity,
          rotation,
          scale,
          speed,
          angularSpeed,
          lifetime,
          delay,
        })
      }

      particles.generate(data)
      this.node.add(particles.node)

      this._renderers.set(status, particles)
    }
  }

  trigger = (type: StatusType) => {
    if (!this.active) return

    this._actives.push({
      type,
      duration: 0.5,
      time: 0,
    })
  }

  preRender(time: TimeEntity): void {
    if (!this.active) return

    this.node.z = this.world.settings.GRAB_THRESHOLD
    this.node.x = this.world.player.node.x

    for (let i = 0; i < this._actives.length; i++) {
      this._actives[i].time += time.unscaledDeltaTime
    }

    this._actives = this._actives.filter((status) => status.time <= status.duration)
  }

  render(context: RenderContext): void {
    if (!this.active) return

    this.program.use()
    for (const item of this._actives) {
      this.program.uTime(item.time)
      this.program.uGlobalScale(1)
      this._renderers.get(item.type).render(context)
    }
  }
}
